/** 论文台论文论证框架：Brief 为全文 SSOT，章契约 + 门控防跑偏 */

export type ThesisStage =
  | 'briefing' // 开题卡未就绪
  | 'outlining' // 目录/章要点
  | 'drafting' // 分章正文
  | 'revising' // 编审返工
  | 'done'

export type Construct = {
  name: string
  /** 概念定义（文献口径） */
  definition: string
  /** 操作化 / 测量或编码方式 */
  operationalization: string
}

export type ChapterGateStatus =
  | 'locked'
  | 'open'
  | 'drafting'
  | 'review'
  | 'passed'
  | 'needs_revision'

export type ChapterGateReview = {
  at: string
  /** 是否相对契约通过 */
  passed: boolean
  /** 相对研究问题/框架的对齐分 0–100（模型或人工） */
  alignScore: number
  findings: string[]
  /** 建议修改点 */
  revisions: string[]
}

export type ChapterGateState = {
  status: ChapterGateStatus
  /** 章摘要：门控通过后写入，供后章注入 */
  summary: string
  lastReview?: ChapterGateReview
}

/** 开题卡：问题—框架—方法—边界（整篇论证的唯一真相源） */
export type ThesisBrief = {
  novelId: string
  updatedAt: string
  stage: ThesisStage

  /** 实践情境：企业/行业/岗位背景 */
  practiceBackground: string
  /** 管理痛点（可观察、可行动，非空泛「数字化很重要」） */
  problemStatement: string
  /** 研究目的（一句话） */
  researchPurpose: string
  /** 研究问题 RQ1…（主问题可放第一项） */
  researchQuestions: string[]
  /** 理论视角 / 主理论 */
  theoreticalLens: string
  /** 关键构念 */
  keyConstructs: Construct[]
  /** 假设或命题；探索性案例可空，但须在 scopeOut 说明 */
  hypothesesOrPropositions: string[]
  /** 分析框架（如何把构念串成可检验逻辑） */
  analysisFramework: string

  methodType: string
  unitOfAnalysis: string
  caseOrSample: string
  dataSources: string[]
  analysisPlan: string

  /** 写什么 */
  scopeIn: string
  /** 明确不写什么——防论证漂移的硬边界 */
  scopeOut: string
  expectedContributions: string[]

  /**
   * 章门控状态，key 为章序号字符串 "1"…"6"
   * （与 MBA 六章契约对齐；目录标题可改，序号契约不变）
   */
  chapterGates: Record<string, ChapterGateState>
}

export type ChapterContract = {
  chapterIndex: 1 | 2 | 3 | 4 | 5 | 6
  title: string
  /** 在整篇论证链中的角色 */
  roleInArgument: string
  /** 本章必须回答的问题 */
  mustAnswer: string[]
  /** 必备结构块 */
  mustInclude: string[]
  /** 必须回扣 Brief 的字段语义 */
  mustLinkToBrief: Array<
    | 'practiceBackground'
    | 'problemStatement'
    | 'researchQuestions'
    | 'theoreticalLens'
    | 'keyConstructs'
    | 'hypothesesOrPropositions'
    | 'analysisFramework'
    | 'methodType'
    | 'caseOrSample'
    | 'dataSources'
    | 'scopeOut'
    | 'expectedContributions'
  >
  /** 禁止事项（跑偏红线） */
  forbid: string[]
  /** 依赖的前序章（须至少 open/passed） */
  dependsOn: number[]
  /** 门控通过条件 */
  exitCriteria: string[]
  suggestedSections: string[]
}

export function emptyGate(status: ChapterGateStatus = 'locked'): ChapterGateState {
  return { status, summary: '' }
}

export function createEmptyBrief(novelId: string): ThesisBrief {
  const gates: Record<string, ChapterGateState> = {}
  for (let i = 1; i <= 6; i++) {
    gates[String(i)] = emptyGate(i === 1 ? 'open' : 'locked')
  }
  return {
    novelId,
    updatedAt: new Date().toISOString(),
    stage: 'briefing',
    practiceBackground: '',
    problemStatement: '',
    researchPurpose: '',
    researchQuestions: [''],
    theoreticalLens: '',
    keyConstructs: [{ name: '', definition: '', operationalization: '' }],
    hypothesesOrPropositions: [],
    analysisFramework: '',
    methodType: '案例研究',
    unitOfAnalysis: '',
    caseOrSample: '',
    dataSources: [],
    analysisPlan: '',
    scopeIn: '',
    scopeOut: '',
    expectedContributions: [],
    chapterGates: gates,
  }
}
